import {
  readableTextOnBackground,
  mutedSidebarTextOnBackground,
  defaultMainTextForTheme,
  mainContentBackgroundForTheme,
  ensureContrastOnBackground,
  normalizeHex,
} from './colorContrast';

export function blendColor(color1, color2, ratio) {
  const hex1 = color1.replace('#', '');
  const hex2 = color2.replace('#', '');
  const r1 = parseInt(hex1.substring(0, 2), 16);
  const g1 = parseInt(hex1.substring(2, 4), 16);
  const b1 = parseInt(hex1.substring(4, 6), 16);
  const r2 = parseInt(hex2.substring(0, 2), 16);
  const g2 = parseInt(hex2.substring(2, 4), 16);
  const b2 = parseInt(hex2.substring(4, 6), 16);
  const r = Math.round(r1 * (1 - ratio) + r2 * ratio);
  const g = Math.round(g1 * (1 - ratio) + g2 * ratio);
  const b = Math.round(b1 * (1 - ratio) + b2 * ratio);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

export function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Applies branding CSS variables from localStorage (keys match SettingsModal).
 * Sidebar text is chosen for contrast on the secondary color; main text uses theme defaults
 * or an adjusted custom font color for readability on the main content background.
 */
export function applyBrandingFromStorage() {
  const rawP = localStorage.getItem('branding_primary_color') || localStorage.getItem('branding_primary') || null;
  const rawS = localStorage.getItem('branding_secondary_color') || localStorage.getItem('branding_secondary') || null;
  const rawF = localStorage.getItem('branding_font_color') || null;
  const primaryColor = rawP ? normalizeHex(rawP) : null;
  const secondaryColor = rawS ? normalizeHex(rawS) : null;
  const fontColor = rawF ? normalizeHex(rawF) : null;
  const root = document.documentElement;
  const isDark = localStorage.getItem('theme') === 'dark' || root.getAttribute('data-theme') === 'dark';
  const mainBg = mainContentBackgroundForTheme(isDark);

  if (primaryColor) {
    root.style.setProperty('--primary', primaryColor, 'important');
    /* Keep --accent as Plutoo marketing coral for CTAs; primary is for text/links */
    root.style.setProperty('--info', primaryColor, 'important');
    root.style.setProperty('--sidebar-icon-bg', hexToRgba(primaryColor, 0.15), 'important');
  }

  if (secondaryColor) {
    root.style.setProperty('--sidebar-bg', secondaryColor, 'important');
    const st = readableTextOnBackground(secondaryColor);
    const stm = mutedSidebarTextOnBackground(secondaryColor);
    root.style.setProperty('--sidebar-text', st, 'important');
    root.style.setProperty('--sidebar-text-muted', stm, 'important');
    const hoverColor =
      secondaryColor === '#3771cd' || secondaryColor === '#33475b'
        ? '#2d5aa0'
        : secondaryColor === '#374151'
          ? '#4B5563'
          : secondaryColor === '#000000'
            ? '#1a1a1a'
            : secondaryColor === '#001F3F'
              ? '#003366'
              : secondaryColor === '#f5f4f0' || secondaryColor === '#faf9f7' || secondaryColor === '#fdfcf9'
                ? blendColor(secondaryColor, '#33475b', 0.12)
                : blendColor(secondaryColor, primaryColor || '#000000', 0.15);
    root.style.setProperty('--sidebar-hover', hoverColor, 'important');
  }

  if (!isDark) {
    root.style.setProperty('--sidebar-active', '#ff7a59', 'important');
  }

  /* Dark theme: fixed sidebar (marketing-style contrast), ignore light sidebar fill */
  if (isDark) {
    root.style.setProperty('--sidebar-bg', '#141416', 'important');
    root.style.setProperty('--sidebar-text', '#f4f4f5', 'important');
    root.style.setProperty('--sidebar-text-muted', '#a1a1aa', 'important');
    root.style.setProperty('--sidebar-hover', 'rgba(255, 255, 255, 0.08)', 'important');
    root.style.setProperty('--sidebar-active', '#ff7a59', 'important');
  }

  if (fontColor) {
    const safe = ensureContrastOnBackground(fontColor, mainBg, 4.5);
    root.style.setProperty('--text-dark', safe, 'important');
  } else {
    root.style.setProperty('--text-dark', defaultMainTextForTheme(isDark), 'important');
  }
}
